import { formatCode } from '@pl/domain';
import type { CountHiddenDto, CountProgressDto, CountResultDto, ReasonCode } from '@pl/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, api } from '../../lib/api';
import { canceledNotice } from '../../lib/cancelCount';
import { qk, useCountProgress, useSkuMap } from '../../lib/queries';
import { Empty, ErrorNote, RefetchNote, Spinner } from '../../ui/bits';
import { isLight } from '../../ui/color';
import { Icon } from '../../ui/Icon';
import { ScannerView } from '../scanner/ScannerView';
import { useScanSite } from '../useScanSite';
import { countContext, enterCount } from './countKit';
import { type FatalTable, kit, plural, RevealedResult, ScanRedScreen, ScanSheet, useScanFlow } from './scanKit';
import s from './CountScan.module.css';

const RED_TITLE: Partial<Record<ReasonCode, string>> = {
  PALLET_NOT_ALLOWED: 'Scan boxes, not pallets',
  VOIDED: 'Voided sticker',
  UNKNOWN_CODE: 'Unknown sticker',
  INVALID_CODE: 'Unknown sticker',
};

// No COUNT_CLOSED here: a count that ended under the phone is taken by `onJobEnded`, not a red screen.
const COUNT_FATAL: FatalTable = {
  WRONG_SITE: { title: 'Wrong site', note: 'Nothing was counted. This count belongs to another site.' },
  NOT_FOUND: { title: 'Count not found', note: 'Nothing was counted. This count is no longer on the server.' },
  FORBIDDEN: { title: 'Not your site', note: 'Nothing was counted. You do not have access to this count’s site.' },
  IDEMPOTENCY_MISMATCH: { title: 'Scan id reused', note: 'Nothing was counted. This is a bug in the app — report it before carrying on.' },
};

const SET_ASIDE = 'Nothing was counted. Set this sticker aside.';

/**
 * A blind count: scan every box of one SKU (or on one pallet) you can find.
 *
 * The one number on this screen is how many the worker has scanned. What the count should come to
 * is never shown and never asked for — this screen reads the count's progress and nothing else that
 * could carry a stock figure, and it re-asks only for that when a scan lands. It reads the progress
 * (`GET /counts/:id/progress`), never the full count, even of a count that has ended: it can land on
 * one finished elsewhere, and all it may learn then is that it ended. A counter who can see the answer
 * counts to it; the whole point of the count is that they cannot.
 *
 * Finish is locked while any scan for this count is still on the phone: finishing under it would
 * call a box missing that is sitting in the queue.
 */
export function CountScan() {
  const { countId = '' } = useParams();
  const { site } = useScanSite();
  const qc = useQueryClient();
  const nav = useNavigate();
  const query = useCountProgress(countId);
  const skuById = useSkuMap();

  // Going into a count, however the worker got here — Start, a join, a resume: from now until the
  // site's open counts are asked for again, the count home trusts no list saying nothing is open.
  // Not once it has ended: Finish's own answer would otherwise end the exemption it just made.
  // Recorded against the count's own site, which is the one whose lists it belongs in: a count at
  // another site opened here must not be patched into this site's open counts.
  const status = query.data?.status;
  const countSite = query.data?.siteId;
  useEffect(() => {
    if (countSite && status !== 'finished' && status !== 'cancelled') enterCount(qc, countSite, qc.getQueryData<CountProgressDto>(qk.countProgress(countId)));
  }, [qc, countSite, countId, status]);

  // A lead, or the end of the day, canceled it: nothing was recorded, so there is nothing to see
  // here. Back to the list, saying who ended it — never "finished", which it was not.
  const cancelledBy = status === 'cancelled' ? query.data?.cancelledBy : undefined;
  useEffect(() => {
    if (status !== 'cancelled') return;
    // The lists the phone stamped as it went into this count still have it in progress, and would
    // stand for their 15 s if the cancel was learned some way other than the signal.
    void qc.invalidateQueries({ queryKey: ['counts'] });
    nav('/scan/count', { replace: true, state: { notice: canceledNotice(cancelledBy ?? null) } });
  }, [nav, qc, status, cancelledBy]);

  // A scan refused because the count has ended — canceled or finished while the phone's stream was
  // down — is the count ending under the worker, not a refusal of this box. No red screen, not for
  // one frame: the scan settling re-reads the count (`refetchKeys`), and the phone leaves it the way
  // it would on the signal.
  const [ending, setEnding] = useState(false);
  const scan = useScanFlow(
    {
      context: countContext(countId),
      contextId: countId,
      // Only the count itself: anything else it touched could carry the figure it is blind to.
      refetchKeys: [qk.countProgress(countId)],
      onResponse: (response) => {
        const tally = response.count?.scanned;
        if (tally !== undefined) qc.setQueryData<CountProgressDto>(qk.countProgress(countId), (prev) => (prev ? { ...prev, scanned: tally } : prev));
      },
      onJobEnded: (error) => {
        if (error.code !== 'COUNT_CLOSED') return false;
        setEnding(true);
        return true;
      },
    },
    {
      mode: 'count',
      refused: 'Not counted',
      locks: 'Finish',
      refusalNote: SET_ASIDE,
      unchanged: 'Nothing was counted',
      titles: RED_TITLE,
      fallbackTitle: "Can't count this sticker",
      fatal: COUNT_FATAL,
      notTaken: 'Nothing recorded for this sticker.',
      // Quiet green for a box that counts; a box of another SKU, or one already counted, is amber.
      accepted: (response, code) => ({ tone: 'ok', title: response.message, message: 'Keep scanning.', code }),
    },
  );
  const { pending, red, setRed } = scan;

  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const keepCounting = useRef<HTMLButtonElement>(null);

  /**
   * A finish refused with `COUNT_CLOSED` found the count already ended: this phone's own Finish whose
   * answer was lost, someone else's in a joined count, or a cancel. Which it was, the progress cannot
   * say and must not: it carries no result. So the screen says the count ended, and the result is
   * behind the result screen's gate like any other.
   */
  const finish = useMutation({
    mutationFn: async (): Promise<{ result: CountResultDto | CountHiddenDto } | { ended: CountProgressDto }> => {
      try {
        return { result: await api.post<CountResultDto | CountHiddenDto>(`/counts/${countId}/finish`) };
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== 'COUNT_CLOSED') throw err;
        const now = await api.get<CountProgressDto>(`/counts/${countId}/progress`).catch(() => null);
        if (!now || now.status === 'open') throw err;
        return { ended: now };
      }
    },
    onSuccess: (out) => {
      setConfirmEmpty(false);
      if ('ended' in out) {
        qc.setQueryData(qk.countProgress(countId), out.ended);
        return;
      }
      // The result screen asks the server for the result itself (see `CountResult`): Finish's own
      // answer is not handed over, since the server may be withholding it from this surface.
      qc.setQueryData<CountProgressDto>(qk.countProgress(countId), (prev) => (prev ? { ...prev, status: 'finished', scanned: null } : prev));
      // Marked stale, not fetched: nothing showing them is mounted while this screen is.
      void qc.invalidateQueries({ queryKey: ['counts'] });
      void qc.invalidateQueries({ queryKey: ['counts-suggested'] });
      void qc.invalidateQueries({ queryKey: ['discrepancies'] });
      nav(`/scan/count/${countId}/result`, { replace: true });
    },
  });

  if (!site) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Empty title="No site on this account" description="Counts work from a site. Ask an admin to add you to one." />
      </div>
    );
  }
  if (query.isPending) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Spinner label="Loading the count" />
      </div>
    );
  }
  if (query.isLoadingError || !query.data) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <div className={kit.tkStack}>
          <ErrorNote error={query.error} />
          <button type="button" className={`bigbtn ghost ${kit.tkRetry}`} onClick={() => void query.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const c = query.data;
  // Ended elsewhere, or by this phone's own Finish whose answer was lost: that it ended is all this
  // screen knows. What it found is the result screen's, behind its gate.
  if (c.status === 'finished') {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Empty
          title="This count has ended"
          description="Its result opens when nothing is being counted here."
          action={
            <>
              <Link to={`/scan/count/${c.id}/result`} className="bigbtn ghost">
                See the result
              </Link>
              <Link to="/scan/count" className="bigbtn ghost">
                Back to counts
              </Link>
            </>
          }
        />
      </div>
    );
  }
  // On its way back to the list (the effect above).
  if (c.status === 'cancelled') {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Spinner label="Back to counts" />
      </div>
    );
  }
  if (c.siteId !== site.id) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Empty
          title="This count belongs to another site"
          description="Switch this phone to that site to carry on."
          action={
            <Link to="/scan/count" className="bigbtn ghost">
              Back to counts
            </Link>
          }
        />
      </div>
    );
  }

  // The server said this count has ended; the phone is asking how, to leave it the right way.
  if (ending) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        {query.isRefetchError ? (
          <Empty
            title="Couldn’t check this count"
            description="The server would not take the last scan. Nothing was counted."
            action={
              <>
                <button type="button" className="bigbtn ghost" onClick={() => void query.refetch()}>
                  Try again
                </button>
                <Link to="/scan/count" className="bigbtn ghost">
                  Back to counts
                </Link>
              </>
            }
          />
        ) : (
          <Spinner label="Checking this count" />
        )}
      </div>
    );
  }

  const sku = c.skuId ? skuById.get(c.skuId) : undefined;
  const pallet = c.scope === 'pallet';
  const hex = pallet ? null : (sku?.colourHex ?? null);
  const light = isLight(hex);
  const busy = finish.isPending;
  const locked = pending > 0;

  return (
    <>
      {pending > 0 && (
        <div className={`offline ${s.waitBanner}`}>
          <Icon name="wifi-off" />
          <p className={s.waitText}>{`${plural(pending, 'scan', 'scans')} waiting for connection. Finish unlocks when synced.`}</p>
        </div>
      )}

      <section
        className={`band ${s.countBand}${pallet ? ` ${s.palletBand}` : ''}`}
        style={pallet ? undefined : { background: hex ?? 'var(--paper-2)', color: light ? 'var(--ink)' : '#fff' }}
        aria-label="Scanned so far"
      >
        <div className={s.bandWho}>
          <div className="clr-xl">{pallet ? 'PALLET' : (sku?.colourName ?? 'NO COLOR').toUpperCase()}</div>
          <div className={`skul ${s.bandName}`}>
            {pallet ? <span className="mono">{formatCode(c.palletCode ?? '')}</span> : (sku?.name ?? 'Loading…').toUpperCase()}
            {!pallet && sku && <span className="mono">{sku.code}</span>}
          </div>
        </div>
        <div className="n">
          <b>{c.scanned}</b>
          <span>scanned</span>
        </div>
      </section>

      <div className={`scan-body pt ${s.scanBody}`}>
        <RefetchNote queries={[query]} />
        <p className={s.hiddenNote}>
          <Icon name="eye-off" size={20} />
          <span>Expected count is hidden until you finish</span>
        </p>

        {/* Stopped while a red outcome holds the screen, so the next sticker cannot slip past unread
            and a wedge scanner's Enter cannot double as the dismissal — and while Finish is on its
            way, since a box read then lands on a closed count behind the result screen. */}
        <ScannerView onCode={scan.handleCode} enabled={red === null && !finish.isPending} />
        <RevealedResult result={scan.shownResult} revealOn={scan.result} />
      </div>

      <div className="scan-foot">
        <ErrorNote error={finish.error} />
        <button
          type="button"
          className={`bigbtn${locked ? ' locked' : ''}`}
          disabled={locked || busy}
          onClick={() => (c.scanned === 0 ? setConfirmEmpty(true) : finish.mutate())}
        >
          {locked && <Icon name="lock" />}
          Finish count
        </button>
      </div>

      {/* A count finished with nothing scanned lists every box as missing. That is sometimes the
          truth, but it is never something to do with a stray thumb. The sheet says so without
          saying how many. */}
      <ScanSheet
        open={confirmEmpty}
        onClose={() => {
          setConfirmEmpty(false);
          finish.reset();
        }}
        busy={busy}
        safeFocus={keepCounting}
        title="Finish with nothing scanned?"
        lead="Every box recorded here for this count is listed as missing, for a lead to review."
        actions={
          <>
            <ErrorNote error={finish.error} />
            <button type="button" className="bigbtn" disabled={busy || locked} onClick={() => finish.mutate()}>
              Finish count
            </button>
            <button type="button" ref={keepCounting} className="bigbtn ghost" disabled={busy} onClick={() => setConfirmEmpty(false)}>
              Keep counting
            </button>
          </>
        }
      />

      <ScanRedScreen red={red} resumeLabel="Got it, keep counting" back={{ to: '/scan/count', label: 'Back to counts' }} onDismiss={() => setRed(null)} />
    </>
  );
}
